import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FikoThreeJsComponent } from '../../fiko-three-js.component';
import { FikoMetinNotuCizgisi } from './FikoMetinNotuCizgisi';
import * as THREE from 'three';

/**
 * CATIA-style text note annotation dialog
 * Similar to FikoOlcumComponent pattern
 */
@Component({
  selector: 'app-fiko-metin-notu',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './fiko-metin-notu.component.html',
  styleUrls: ['./fiko-metin-notu.component.css']
})
export class FikoMetinNotuComponent implements OnInit, OnDestroy {
  /** 3D viewer reference */
  @Input() anaGosterim: FikoThreeJsComponent;

  /** Dialog wrapper instance */
  @Input() pencere: any;

  /** Note text content */
  notMetni: string = 'Yeni not...';

  /** Text color - Professional CAD: white text for readability */
  renk: string = '#FFFFFF';

  /** Font size - Professional CAD: compact 12px default */
  boyut: number = 12;

  /** Background color - Professional CAD: semi-transparent dark */
  arkaplanRengi: string = '#1a1a1a';
  arkaplanOpaklik: number = 85;

  /** Border color - Professional CAD: subtle border */
  cercevRengi: string = '#666666';
  cercevKalinlik: number = 1;

  /** Font settings - Professional CAD: clean sans-serif */
  fontAilesi: string = 'Arial';
  fontKalin: boolean = false;
  fontItalik: boolean = false;
  hizalama: 'left' | 'center' | 'right' = 'left';
  padding: number = 8;  // Compact padding

  /** Leader line settings - Professional CAD: thin clean line */
  okGoster: boolean = true;
  okRengi: string = '#808080';  // Gray line like CAD
  okKalinlik: number = 1;
  okTipi: 'solid' | 'dashed' | 'dotted' = 'solid';

  /** Shadow settings - Professional CAD: subtle or no shadow */
  golgeGoster: boolean = false;
  golgeMesafe: number = 2;

  /** Sabit boyut - kamera zoom'una göre scale ayarlanır */
  sabitBoyut: boolean = true;

  /** Point selection */
  nokta: THREE.Vector3 | null = null;
  noktaSecimModu: boolean = false;

  /** Tab states */
  sekmeler = {
    gorunum: true,
    stil: false,
    cizim: false
  };

  /** Active note instance */
  aktifNot: FikoMetinNotuCizgisi | null = null;

  /** Preview note instance */
  onizlemeNot: FikoMetinNotuCizgisi | null = null;

  /** Available preset colors */
  renkler = [
    { isim: 'Altın Sarı', kod: '#FFD700' },
    { isim: 'Cyan', kod: '#00FFFF' },
    { isim: 'Turuncu', kod: '#FF8C00' },
    { isim: 'Kırmızı', kod: '#FF0000' },
    { isim: 'Yeşil', kod: '#00FF00' },
    { isim: 'Mavi', kod: '#1E90FF' },
    { isim: 'Beyaz', kod: '#FFFFFF' }
  ];

  private dialogCloseSub: Subscription;

  ngOnInit(): void {
    // Subscribe to dialog close event
    const dialogInstance = this.pencere?.instance || this.pencere;
    if (dialogInstance?.onDialogClose) {
      this.dialogCloseSub = dialogInstance.onDialogClose.subscribe(() => {
        this.temizle();
      });
    }

    // Load settings from localStorage
    this.ayarlariYukle();
  }

  ngOnDestroy(): void {
    if (this.noktaSecimModu) {
      this.noktaSecimModunuKapat();
    }
    this.temizle();
  }

  /**
   * Start point selection mode
   */
  noktaSec(): void {
    this.noktaSecimModu = true;
    const canvas = this.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'crosshair';
      canvas.addEventListener('click', this.noktaTiklama);
    }
  }

  /**
   * Handle point click
   */
  private noktaTiklama = (event: MouseEvent) => {
    const position = this.get3DPositionFromMouse(event);
    if (!position) return;

    this.nokta = position;

    this.noktaSecimModunuKapat();
    this.onizlemeGuncelle();
  };

  /**
   * Close point selection mode
   */
  private noktaSecimModunuKapat(): void {
    this.noktaSecimModu = false;
    const canvas = this.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'default';
      canvas.removeEventListener('click', this.noktaTiklama);
    }
  }

  /**
   * Get 3D position from mouse click
   * Uses deep traversal to find all mesh objects and applies matrixWorld transform
   */
  private get3DPositionFromMouse(event: MouseEvent): THREE.Vector3 | null {
    if (!this.anaGosterim?.sunucu || !this.anaGosterim?.aktifKamera) return null;

    const canvas = this.anaGosterim.sunucu.domElement;
    const rect = canvas.getBoundingClientRect();

    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.anaGosterim.aktifKamera);

    const sahne = this.anaGosterim.sahneler?.[0];
    if (!sahne) return null;

    // Collect ALL meshes recursively (not just direct children)
    const meshes: THREE.Object3D[] = [];
    sahne.traverse((obj: THREE.Object3D) => {
      if ((obj as any).isMesh || obj.type === 'Mesh') {
        meshes.push(obj);
      }
    });

    // Perform raycast against all meshes
    const intersects = raycaster.intersectObjects(meshes, false); // false because we already traversed

    if (intersects.length > 0) {
      const hit = intersects[0];

      // The hit.point is already in world coordinates, so just clone it
      const worldPoint = hit.point.clone();

      return worldPoint;
    }

    // Fallback - project to a plane at z=0 or use camera distance

    // Create a plane at z=0 or at average scene depth
    const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    const intersection = new THREE.Vector3();

    if (raycaster.ray.intersectPlane(plane, intersection)) {
      return intersection;
    }

    // Ultimate fallback - place at camera direction
    const distance = 5;
    const direction = new THREE.Vector3();
    this.anaGosterim.aktifKamera.getWorldDirection(direction);
    const position = this.anaGosterim.aktifKamera.position.clone();
    position.add(direction.multiplyScalar(distance));

    return position;
  }

  /**
   * Calculate text offset from target point based on camera view
   * Returns an offset vector that positions the text label above and to the right
   * Professional CAD style: compact, clean positioning like CATIA/SolidWorks
   */
  private hesaplaMetinOffset(): THREE.Vector3 {
    const kamera = this.anaGosterim?.aktifKamera;
    if (!kamera) {
      return new THREE.Vector3(0.3, 0.2, 0);
    }

    // Calculate offset based on camera zoom and view size
    const zoom = kamera.zoom || 1;
    const viewHeight = ((kamera as any).top - (kamera as any).bottom) / zoom;

    // Professional CAD style: small offset (2-3% of view height)
    // Creates a compact, professional appearance like real CAD software
    const offsetX = viewHeight * 0.025;  // Small horizontal offset
    const offsetY = viewHeight * 0.015;  // Small vertical offset

    // Return offset - text will be positioned slightly above-right of target point
    return new THREE.Vector3(offsetX, offsetY, 0);
  }

  /**
   * Update preview note
   */
  onizlemeGuncelle(): void {
    if (!this.nokta) return;

    const sahne = this.anaGosterim?.sahneler?.[0];
    if (!sahne) return;

    // Remove old preview
    if (this.onizlemeNot) {
      sahne.remove(this.onizlemeNot);
      this.onizlemeNot.dispose();
      this.onizlemeNot = null;
    }

    // Convert background color with opacity
    const hex = this.arkaplanRengi.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const arkaplanRgba = `rgba(${r}, ${g}, ${b}, ${this.arkaplanOpaklik / 100})`;

    // Create preview note
    this.onizlemeNot = new FikoMetinNotuCizgisi(
      sahne as any,
      this.notMetni,
      {
        renk: this.renk,
        boyut: this.boyut,
        arkaplanRengi: arkaplanRgba,
        cercevRengi: this.cercevRengi,
        okGoster: this.okGoster
      }
    );

    // Apply all settings
    this.onizlemeNot.setBorderStyle(this.cercevRengi, this.cercevKalinlik);
    this.onizlemeNot.setFontStyle({
      ailesi: this.fontAilesi,
      kalin: this.fontKalin,
      italik: this.fontItalik,
      hizalama: this.hizalama,
      padding: this.padding
    });
    this.onizlemeNot.setLeaderStyle({
      renk: this.okRengi,
      kalinlik: this.okKalinlik,
      tip: this.okTipi
    });
    this.onizlemeNot.setShadow(this.golgeGoster, this.golgeMesafe);
    this.onizlemeNot.setSabitBoyut(this.sabitBoyut);

    // Set target point with calculated offset for leader line
    const offset = this.hesaplaMetinOffset();
    this.onizlemeNot.setHedefNokta(this.nokta, offset);

    sahne.add(this.onizlemeNot);
    this.anaGosterim?.ciz();
  }

  /**
   * Add note to 3D scene
   */
  notEkle(): void {
    if (!this.nokta) {
      alert('Lütfen önce bir konum noktası seçin!');
      return;
    }

    if (!this.anaGosterim?.sahneler?.[0]) {
      return;
    }

    const sahne = this.anaGosterim.sahneler[0] as any;

    // Save settings
    this.ayarlariKaydet();

    // Convert background color with opacity
    const hex = this.arkaplanRengi.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const arkaplanRgba = `rgba(${r}, ${g}, ${b}, ${this.arkaplanOpaklik / 100})`;

    // Create new note instance
    const not = new FikoMetinNotuCizgisi(
      sahne,
      this.notMetni,
      {
        renk: this.renk,
        boyut: this.boyut,
        arkaplanRengi: arkaplanRgba,
        cercevRengi: this.cercevRengi,
        okGoster: this.okGoster
      }
    );

    // Apply all additional settings after creation
    not.setBorderStyle(this.cercevRengi, this.cercevKalinlik);
    not.setFontStyle({
      ailesi: this.fontAilesi,
      kalin: this.fontKalin,
      italik: this.fontItalik,
      hizalama: this.hizalama,
      padding: this.padding
    });
    not.setLeaderStyle({
      renk: this.okRengi,
      kalinlik: this.okKalinlik,
      tip: this.okTipi
    });
    not.setShadow(this.golgeGoster, this.golgeMesafe);
    not.setSabitBoyut(this.sabitBoyut);

    // Set target point with offset for leader line
    const offset = this.hesaplaMetinOffset();
    not.setHedefNokta(this.nokta, offset);

    // Add to annotations group
    const etiketler = sahne.fikoEtiketler;
    if (etiketler) {
      const metin = etiketler.ekleMetinNotu(`Metin Notu.${etiketler.sayac}`);
      metin.userData['FikoMetinNotuCizgisi'] = not;
      not.parent = metin;

      // Add to scene
      sahne.add(not);

      // Render
      this.anaGosterim.ciz();
      this.anaGosterim?.FikoYT?.degisti?.emit();
    }

    this.aktifNot = not;

    // Reset point and preview
    this.nokta = null;
    if (this.onizlemeNot) {
      sahne.remove(this.onizlemeNot);
      this.onizlemeNot.dispose();
      this.onizlemeNot = null;
    }

    // Reset form
    this.notMetni = 'Yeni not...';
  }

  /**
   * Remove active note
   */
  notSil(): void {
    if (this.aktifNot && this.anaGosterim?.sahneler?.[0]) {
      this.aktifNot.dispose();
      this.anaGosterim.sahneler[0].remove(this.aktifNot);
      this.anaGosterim.ciz();
      this.aktifNot = null;
    }
  }

  /**
   * Update note color
   */
  renkDegistir(yeniRenk: string): void {
    this.renk = yeniRenk;
    if (this.aktifNot) {
      this.aktifNot.setColor(yeniRenk);
      this.anaGosterim?.ciz();
    }
    this.onizlemeGuncelle();
  }

  /**
   * Update note size
   */
  boyutDegistir(): void {
    if (this.aktifNot) {
      this.aktifNot.setSize(this.boyut);
      this.anaGosterim?.ciz();
    }
    this.onizlemeGuncelle();
  }

  /**
   * Update note text
   */
  metniGuncelle(): void {
    if (this.aktifNot) {
      this.aktifNot.setText(this.notMetni);
      this.anaGosterim?.ciz();
    }
    this.onizlemeGuncelle();
  }

  /**
   * Toggle leader line visibility
   */
  okDurumDegistir(): void {
    if (this.aktifNot) {
      this.aktifNot.setLeaderVisible(this.okGoster);
      this.anaGosterim?.ciz();
    }
    this.onizlemeGuncelle();
  }

  /**
   * Update background color and opacity
   */
  arkaplanDegistir(): void {
    if (this.aktifNot) {
      // Convert hex to rgba with opacity
      const hex = this.arkaplanRengi.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const rgba = `rgba(${r}, ${g}, ${b}, ${this.arkaplanOpaklik / 100})`;

      this.aktifNot.setBackgroundColor(rgba);
      this.anaGosterim?.ciz();
    }
    this.onizlemeGuncelle();
  }

  /**
   * Update border style
   */
  cerceveDegistir(): void {
    if (this.aktifNot) {
      this.aktifNot.setBorderStyle(this.cercevRengi, this.cercevKalinlik);
      this.anaGosterim?.ciz();
    }
    this.onizlemeGuncelle();
  }

  /**
   * Update font and alignment styles
   */
  stilDegistir(): void {
    if (this.aktifNot) {
      this.aktifNot.setFontStyle({
        ailesi: this.fontAilesi,
        kalin: this.fontKalin,
        italik: this.fontItalik,
        hizalama: this.hizalama,
        padding: this.padding
      });
      this.anaGosterim?.ciz();
    }
    this.onizlemeGuncelle();
  }

  /**
   * Update leader line style
   */
  okStilDegistir(): void {
    if (this.aktifNot) {
      this.aktifNot.setLeaderStyle({
        renk: this.okRengi,
        kalinlik: this.okKalinlik,
        tip: this.okTipi
      });
      this.anaGosterim?.ciz();
    }
    this.onizlemeGuncelle();
  }

  /**
   * Update shadow settings
   */
  golgeDegistir(): void {
    if (this.aktifNot) {
      this.aktifNot.setShadow(this.golgeGoster, this.golgeMesafe);
      this.anaGosterim?.ciz();
    }
    this.onizlemeGuncelle();
  }

  /**
   * Toggle sabit boyut (fixed size regardless of zoom)
   */
  sabitBoyutDegistir(): void {
    if (this.aktifNot) {
      this.aktifNot.setSabitBoyut(this.sabitBoyut);
      this.anaGosterim?.ciz();
    }
    this.onizlemeGuncelle();
  }

  /**
   * Update active note with current settings
   */
  notGuncelle(): void {
    if (!this.aktifNot) return;

    this.aktifNot.setText(this.notMetni);
    this.aktifNot.setColor(this.renk);
    this.aktifNot.setSize(this.boyut);

    // Convert background color
    const hex = this.arkaplanRengi.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const rgba = `rgba(${r}, ${g}, ${b}, ${this.arkaplanOpaklik / 100})`;

    this.aktifNot.setBackgroundColor(rgba);
    this.aktifNot.setBorderStyle(this.cercevRengi, this.cercevKalinlik);
    this.aktifNot.setFontStyle({
      ailesi: this.fontAilesi,
      kalin: this.fontKalin,
      italik: this.fontItalik,
      hizalama: this.hizalama,
      padding: this.padding
    });
    this.aktifNot.setLeaderVisible(this.okGoster);
    this.aktifNot.setLeaderStyle({
      renk: this.okRengi,
      kalinlik: this.okKalinlik,
      tip: this.okTipi
    });
    this.aktifNot.setShadow(this.golgeGoster, this.golgeMesafe);

    this.anaGosterim?.ciz();
  }

  /**
   * Close dialog
   */
  kapat(): void {
    // pencere.instance → FikoDialogCore instance
    // pencere doğrudan da FikoDialogCore olabilir (eski kullanım)
    const dialogInstance = this.pencere?.instance ?? this.pencere;
    if (dialogInstance?.kapa) {
      dialogInstance.kapa();
    } else if (dialogInstance?.kapat) {
      dialogInstance.kapat();
    }
  }

  /**
   * Clean up resources
   */
  private temizle(): void {
    if (this.dialogCloseSub) {
      this.dialogCloseSub.unsubscribe();
    }

    // Clean up preview
    if (this.onizlemeNot) {
      const sahne = this.anaGosterim?.sahneler?.[0];
      if (sahne) {
        sahne.remove(this.onizlemeNot);
      }
      this.onizlemeNot.dispose();
      this.onizlemeNot = null;
    }

    if (this.aktifNot) {
      this.aktifNot.dispose();
      this.aktifNot = null;
    }
  }

  /**
   * Load settings from localStorage
   */
  private ayarlariYukle(): void {
    try {
      const settings = localStorage.getItem('FikoMetinNotu_Ayarlar');
      if (settings) {
        const parsed = JSON.parse(settings);
        this.renk = parsed.renk || '#FFD700';
        this.boyut = parsed.boyut || 14;
        this.arkaplanRengi = parsed.arkaplanRengi || 'rgba(0, 26, 26, 0.9)';
        this.cercevRengi = parsed.cercevRengi || '#FFD700';
        this.okGoster = parsed.okGoster !== undefined ? parsed.okGoster : true;
      }
    } catch (e) {
      // Handle error silently
    }
  }

  /**
   * Save settings to localStorage
   */
  private ayarlariKaydet(): void {
    try {
      const settings = {
        renk: this.renk,
        boyut: this.boyut,
        arkaplanRengi: this.arkaplanRengi,
        cercevRengi: this.cercevRengi,
        okGoster: this.okGoster
      };
      localStorage.setItem('FikoMetinNotu_Ayarlar', JSON.stringify(settings));
    } catch (e) {
      // Handle error silently
    }
  }
}
